import * as React from 'react';
import { connect } from 'react-redux';

import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import DeliverableContentContext, { PAGE_TYPE } from '../../../DeliverableContentContext';

import { removeDeliverableAttachments } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { contentApprovalLinkSelector } from 'selectors/campaign';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';

import type { Deliverable, DeliverableAttachment, Subcampaign } from 'types';

applyTranslations(require('./deliverable-media-approval-context-menu.i18n.json'));

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

type OwnProps = {
  isBulk: boolean,
  isEditMode?: boolean,
  canApprove?: boolean,
  canUpload: boolean,
  subcampaign: Subcampaign,
  deliverable: Deliverable,
  attachments: Array<DeliverableAttachment>,
  hasClientAddedNewFeedback?: boolean,
  onContentSortModeToggle?: () => void,
  onApplyClientFeedback?: (a: Deliverable) => void,
  onDownloadAttachments: (b?: string | null, a?: boolean | null) => void,
  onUploadModeToggled?: () => void,
  onResetApprovalStatus?: (a: Array<DeliverableAttachment>) => void
};

type StateProps = {
  contentApprovalLink: string | undefined | null
};

type DispatchProps = {
  dispatchDeleteFiles: (c: number, b: number, a: Array<DeliverableAttachment>) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm?: () => void,
      onCancel?: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

const msgKey = 'deliverable_media_approval_context_menu';

class DeliverableMediaApprovalContextMenu extends React.PureComponent<Props> {
  handleDownloadAllFiles = () => {
    const { onDownloadAttachments } = this.props;
    onDownloadAttachments();
  };

  handleDownloadApprovedFiles = () => {
    const { onDownloadAttachments } = this.props;
    onDownloadAttachments(undefined, true);
  };

  handleDeleteFiles = () => {
    const {
      subcampaign,
      deliverable,
      attachments,
      dispatchDeleteFiles,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    if (attachments.length > 0) {
      const attachmentsToDelete = attachments.filter(a => a.approvalStatus !== 'approved' && a.approvalStatus !== 'declined');
      const permanentAttachments = attachmentsToDelete.filter(a => (typeof a.id === 'number' && a.id > 0));
      const hasOnlyOnePermanentAttachment = permanentAttachments.length === 1;

      if (permanentAttachments.length === 0) {
        dispatchDeleteFiles(subcampaign.offer.id, deliverable.id, attachmentsToDelete);
        return;
      }

      dispatchShowConfirmationModal({
        message: __t(hasOnlyOnePermanentAttachment
          ? `${msgKey}.messages.delete_file`
          : `${msgKey}.messages.delete_all`
        ),
        confirmButtonLabel: __t(`${msgKey}.buttons.delete`),
        onConfirm: () =>
          dispatchDeleteFiles(subcampaign.offer.id, deliverable.id, attachmentsToDelete)
            .then(() => dispatchHideConfirmationModal()),
        onCancel: () => dispatchHideConfirmationModal()
      });
    }
  };

  handleUploadModeToggled = () => {
    const { onUploadModeToggled } = this.props;

    if (onUploadModeToggled) {
      onUploadModeToggled();
    }
  };

  handleResetApprovalStatus = () => {
    const { attachments, onResetApprovalStatus } = this.props;

    if (onResetApprovalStatus) {
      onResetApprovalStatus(attachments);
    }
  };

  handleApplyClientFeedback = (): void => {
    const { deliverable, onApplyClientFeedback } = this.props;

    if (onApplyClientFeedback) {
      onApplyClientFeedback(deliverable);
    }
  };

  renderContextMenu(page: string): React.ReactNode {
    const {
      isBulk,
      isEditMode,
      canApprove,
      canUpload,
      deliverable,
      attachments,
      hasClientAddedNewFeedback,
      onUploadModeToggled,
      onResetApprovalStatus,
      onContentSortModeToggle,
      contentApprovalLink
    } = this.props;

    const hasAttachments = attachments.length > 0;
    const hasOnlyOneAttachment = attachments.length === 1;
    const hasApprovedAttachments = attachments.some(a => a.approvalStatus === 'approved');
    const attachmentsForReview = attachments.filter(a => a.approvalStatus !== 'approved' && a.approvalStatus !== 'declined');
    const displayClientApprovalItems = onUploadModeToggled
      && !canUpload
      && isBulk
      && page === PAGE_TYPE.CONTENT;

    if (!deliverable) {
      return null;
    }

    let items = [
      ...insertIf(displayClientApprovalItems, {
        icon: 'approval-custom-fg',
        text: __t(`${msgKey}.context_menu.release_content`),
        onClick: onContentSortModeToggle
      }),
      ...insertIf(contentApprovalLink, {
        name: 'open-clients-page',
        type: 'link',
        icon: 'link-external-custom-fg',
        text: __t(`${msgKey}.context_menu.open_client_page`),
        link: contentApprovalLink,
        openInNewTab: true
      }),
      ...insertIf(displayClientApprovalItems, {
        name: 'apply-client-feedback',
        icon: 'rotation-arrows-custom-fg',
        text: __t(`${msgKey}.context_menu.apply_client_feedback`),
        disabled: !hasClientAddedNewFeedback,
        iconClickable: hasClientAddedNewFeedback,
        onClick: this.handleApplyClientFeedback
      }),
      ...insertIf(displayClientApprovalItems || contentApprovalLink, {
        type: 'separator'
      }),
      ...insertIf(
        !canUpload && onResetApprovalStatus,
        {
          icon: 'rotate-left-custom-fg',
          text: __t(hasOnlyOneAttachment ? `${msgKey}.context_menu.reset_approval` : `${msgKey}.context_menu.reset_approval_all`),
          onClick: this.handleResetApprovalStatus
        }
      ),
      ...insertIf(!canUpload && onResetApprovalStatus, {
        type: 'separator'
      }),
      ...insertIf(
        canUpload && attachmentsForReview.length > 0,
        {
          icon: 'trash-custom-fg',
          text: __t(attachmentsForReview.length === 1 ? `${msgKey}.context_menu.delete_file` : `${msgKey}.context_menu.delete_files`),
          onClick: this.handleDeleteFiles
        }
      ),
      ...insertIf(canUpload && attachmentsForReview.length > 0, {
        type: 'separator'
      }),
      ...insertIf(
        hasAttachments,
        {
          icon: 'download-custom-fg',
          text: __t(hasOnlyOneAttachment ? `${msgKey}.context_menu.download_file` : `${msgKey}.context_menu.download_files_all`),
          onClick: this.handleDownloadAllFiles
        }
      ),
      ...insertIf(
        hasApprovedAttachments && isBulk && !isEditMode,
        {
          icon: 'download-custom-fg',
          text: __t(`${msgKey}.context_menu.download_files_approved`),
          onClick: this.handleDownloadApprovedFiles
        }
      )
    ];

    if (onUploadModeToggled) {
      items = [
        ...items,
        ...insertIf(
          items.length > 0 && canApprove,
          {
            type: 'separator'
          }
        ),
        ...insertIf(
          !canUpload && canApprove,
          {
            icon: 'upload-custom-fg',
            text: __t(`${msgKey}.context_menu.enter_upload_mode`),
            onClick: this.handleUploadModeToggled
          }
        ),
        ...insertIf(
          canUpload && canApprove,
          {
            icon: 'upload-custom-fg',
            text: __t(`${msgKey}.context_menu.exit_upload_mode`),
            onClick: this.handleUploadModeToggled
          }
        )
      ];
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <MoreButtonWithMenu
        width={isBulk ? 40 : 20}
        contextMenuProps={{ items }}
      />
    );
  }

  render(): React.ReactNode {
    return (
      <DeliverableContentContext.Consumer>
        {(page) => this.renderContextMenu(page)}
      </DeliverableContentContext.Consumer>
    );
  }
}

const mapStateTopProps = (state): StateProps => ({
  contentApprovalLink: contentApprovalLinkSelector(state)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteFiles: (offerId: number, deliverableId: number, attachments: Array<DeliverableAttachment>) =>
    dispatch(removeDeliverableAttachments(offerId, deliverableId, attachments)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateTopProps, mapDispatchToProps)(DeliverableMediaApprovalContextMenu);
